import { produce } from "immer";
import { SECTION_META, SECTION_VARIANTS, type PageNode, type SectionNode, type SectionStyle, type SectionTone, type TextStyle, type SectionType, type SiteInfo, type WebsiteDefinition } from "@baze/schema";
import { createSection, type DefaultsContext } from "./defaults";
import { nextPageId, nextSectionId, uniqueAnchor, uniquePageSlug, slugifyTitle } from "./ids";
import { getPath, setPathMut } from "./paths";

/**
 * Pure editor operations on a Website Definition (immer: structural sharing, cheap snapshots).
 * Invariants kept by every op: navbar first, footer last, singletons at most once per page,
 * unique section ids (global) and anchors (per page), exactly one home page.
 */

type Def = WebsiteDefinition;

export interface SectionLocation {
  page: PageNode;
  pageIndex: number;
  index: number;
  section: SectionNode;
}

export function locateSection(def: Def, sectionId: string): SectionLocation | null {
  for (let pi = 0; pi < def.pages.length; pi++) {
    const page = def.pages[pi]!;
    const index = page.sections.findIndex((s) => s.id === sectionId);
    if (index !== -1) return { page, pageIndex: pi, index, section: page.sections[index]! };
  }
  return null;
}

export const isSingleton = (type: SectionType) => !!SECTION_META[type].singleton;
const isPinned = (s: SectionNode) => s.type === "navbar" || s.type === "footer";

/** Movable range [min, max] of indices for non-pinned sections on a page. */
function movableRange(page: Pick<PageNode, "sections">): [number, number] {
  const first = page.sections[0]?.type === "navbar" ? 1 : 0;
  const lastIdx = page.sections.length - 1;
  const last = page.sections[lastIdx]?.type === "footer" ? lastIdx - 1 : lastIdx;
  return [first, last];
}

export function canAddSection(page: Pick<PageNode, "sections">, type: SectionType): boolean {
  if (page.sections.length >= 40) return false;
  return !(isSingleton(type) && page.sections.some((s) => s.type === type));
}

/** Clamp an insertion index so navbar stays first and footer last. */
export function insertionIndex(page: Pick<PageNode, "sections">, type: SectionType, index?: number): number {
  if (type === "navbar") return 0;
  const n = page.sections.length;
  if (type === "footer") return n;
  const lo = page.sections[0]?.type === "navbar" ? 1 : 0;
  const hi = page.sections[n - 1]?.type === "footer" ? n - 1 : n;
  return Math.max(lo, Math.min(hi, index ?? hi));
}

export function addSection(def: Def, pageId: string, type: SectionType, index?: number, ctx?: DefaultsContext): { def: Def; id: string | null } {
  const page = def.pages.find((p) => p.id === pageId);
  if (!page || !canAddSection(page, type)) return { def, id: null };
  const section = createSection(def, page.sections, type, ctx);
  const at = insertionIndex(page, type, index);
  const next = produce(def, (d) => {
    d.pages.find((p) => p.id === pageId)!.sections.splice(at, 0, section as never);
  });
  return { def: next, id: section.id };
}

export function removeSection(def: Def, sectionId: string): Def {
  const loc = locateSection(def, sectionId);
  if (!loc) return def;
  return produce(def, (d) => {
    d.pages[loc.pageIndex]!.sections.splice(loc.index, 1);
  });
}

export function duplicateSection(def: Def, sectionId: string): { def: Def; id: string | null } {
  const loc = locateSection(def, sectionId);
  if (!loc || isSingleton(loc.section.type) || loc.page.sections.length >= 40) return { def, id: null };
  const id = nextSectionId(def, loc.section.type);
  const copy = { ...structuredClone(loc.section), id, anchor: uniqueAnchor(loc.page, loc.section.anchor) } as SectionNode;
  const next = produce(def, (d) => {
    d.pages[loc.pageIndex]!.sections.splice(loc.index + 1, 0, copy as never);
  });
  return { def: next, id };
}

/** Move by ±1 within the movable range. Pinned sections (navbar/footer) never move. */
export function moveSection(def: Def, sectionId: string, delta: -1 | 1): Def {
  const loc = locateSection(def, sectionId);
  if (!loc || isPinned(loc.section)) return def;
  const [lo, hi] = movableRange(loc.page);
  const to = loc.index + delta;
  if (to < lo || to > hi) return def;
  return reorderSections(def, loc.page.id, loc.index, to);
}

export function reorderSections(def: Def, pageId: string, from: number, to: number): Def {
  const pi = def.pages.findIndex((p) => p.id === pageId);
  const page = def.pages[pi];
  if (!page || from === to) return def;
  const moving = page.sections[from];
  if (!moving || isPinned(moving)) return def;
  const [lo, hi] = movableRange(page);
  const target = Math.max(lo, Math.min(hi, to));
  if (target === from) return def;
  return produce(def, (d) => {
    const list = d.pages[pi]!.sections;
    const [item] = list.splice(from, 1);
    list.splice(target, 0, item!);
  });
}

export function setHidden(def: Def, sectionId: string, hidden: boolean): Def {
  return patchSection(def, sectionId, (s) => {
    if (hidden) s.hidden = true;
    else delete s.hidden;
  });
}

export function toggleNav(def: Def, sectionId: string, show: boolean): Def {
  return patchSection(def, sectionId, (s) => {
    s.showInNav = show;
  });
}

/** Update any field of a section by path relative to the section ("content.title", "navLabel", "content.items.0.price"). */
export function updateSectionField(def: Def, sectionId: string, path: string, value: unknown): Def {
  if (path === "id" || path === "type") return def;
  const loc = locateSection(def, sectionId);
  if (!loc) return def;
  if (Object.is(getPath(loc.section, path), value)) return def;
  return produce(def, (d) => {
    setPathMut(d.pages[loc.pageIndex]!.sections[loc.index] as unknown as Record<string, unknown>, path, value);
  });
}

export const updateContent = (def: Def, sectionId: string, path: string, value: unknown) => updateSectionField(def, sectionId, path.startsWith("content.") || path === "content" ? path : `content.${path}`, value);

/** Footer chrome (which blocks show, and how they line up) is the same on every page. */
export function updateFooterChrome(def: Def, key: "showSocial" | "showHours" | "showAddress" | "showNav" | "showContact" | "showTitle" | "align", value: boolean | "start" | "center" | "spread"): Def {
  return produce(def, (d) => {
    for (const page of d.pages) {
      for (const section of page.sections) {
        if (section.type !== "footer" || section.content[key] === value) continue;
        section.content = { ...section.content, [key]: value };
      }
    }
  });
}

/** The menu title is the same on every page. */
export function updateNavbarChrome(def: Def, key: "showTitle", value: boolean): Def {
  return produce(def, (d) => {
    for (const page of d.pages) {
      for (const section of page.sections) {
        if (section.type !== "navbar" || section.content[key] === value) continue;
        section.content = { ...section.content, [key]: value };
      }
    }
  });
}

/** Anchors must stay unique per page; conflicting anchors are suffixed. */
export function setAnchor(def: Def, sectionId: string, anchor: string): Def {
  const loc = locateSection(def, sectionId);
  if (!loc) return def;
  const clean = anchor.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/^-+/, "").slice(0, 64);
  if (!clean) return def;
  return updateSectionField(def, sectionId, "anchor", uniqueAnchor(loc.page, clean, sectionId));
}

export function setVariant(def: Def, sectionId: string, variant: string): Def {
  const loc = locateSection(def, sectionId);
  if (!loc || !(SECTION_VARIANTS[loc.section.type] as readonly string[]).includes(variant)) return def;
  return updateSectionField(def, sectionId, "variant", variant);
}

/** Merge owner styling into a section (undefined clears a key; `null` clears all of it). */
export function updateSectionStyle(def: Def, sectionId: string, patch: Partial<SectionStyle> | null): Def {
  return patchSection(def, sectionId, (s) => {
    if (patch === null) {
      delete s.style;
      return;
    }
    const next: Record<string, unknown> = { ...(s.style ?? {}), ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
    if (Object.keys(next).length) s.style = next as SectionStyle;
    else delete s.style;
  });
}

/** Merge a text's style (style.texts[key]); undefined clears a property, `null` the whole entry. */
export function updateTextStyle(def: Def, sectionId: string, key: string, patch: Partial<TextStyle> | null): Def {
  return patchSection(def, sectionId, (s) => {
    const texts: Record<string, TextStyle> = { ...(s.style?.texts ?? {}) };
    if (patch === null) delete texts[key];
    else {
      const next: Record<string, unknown> = { ...(texts[key] ?? {}), ...patch };
      for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
      if (Object.keys(next).length) texts[key] = next as TextStyle;
      else delete texts[key];
    }
    const style: Record<string, unknown> = { ...(s.style ?? {}), texts };
    if (!Object.keys(texts).length) delete style.texts;
    if (Object.keys(style).length) s.style = style as SectionStyle;
    else delete s.style;
  });
}

export function setTone(def: Def, sectionId: string, tone: SectionTone): Def {
  return patchSection(def, sectionId, (s) => {
    if (tone === "default") delete s.tone;
    else s.tone = tone;
  });
}

function patchSection(def: Def, sectionId: string, fn: (s: SectionNode) => void): Def {
  const loc = locateSection(def, sectionId);
  if (!loc) return def;
  return produce(def, (d) => {
    fn(d.pages[loc.pageIndex]!.sections[loc.index] as SectionNode);
  });
}

// ── Site / theme ───────────────────────────────────────────────────────────────

/** Update site info by path ("name", "contact.whatsapp", "social.instagram", "hours"). undefined deletes. */
export function updateSite(def: Def, path: string, value: unknown): Def {
  if (Object.is(getPath(def.site, path), value)) return def;
  return produce(def, (d) => {
    setPathMut(d.site as unknown as Record<string, unknown>, path, value);
    // Keep optional containers tidy (strict schemas accept empty objects, but not undefined values).
    const wa = (d.site as SiteInfo).whatsapp;
    if (wa && Object.keys(wa).length === 0) delete (d.site as SiteInfo).whatsapp;
  });
}

export function updateTheme(def: Def, patch: { id?: string; version?: number; overrides?: WebsiteDefinition["theme"]["overrides"] | null }): Def {
  return produce(def, (d) => {
    if (patch.id) d.theme.id = patch.id;
    if (patch.version) d.theme.version = patch.version;
    if (patch.overrides === null) delete d.theme.overrides;
    else if (patch.overrides) {
      const o = { ...(d.theme.overrides ?? {}), ...patch.overrides };
      for (const k of Object.keys(o) as Array<keyof typeof o>) if (o[k] === undefined) delete o[k];
      if (Object.keys(o).length) d.theme.overrides = o;
      else delete d.theme.overrides;
    }
  });
}

// ── Pages ──────────────────────────────────────────────────────────────────────

export type PagePreset = "tentang" | "layanan" | "kontak" | "donasi" | "formulir" | "kosong";

const FORM_CONTENT = {
  eyebrow: "Formulir",
  title: "Isi formulir",
  description: "Isi data di bawah, lalu kirim lewat WhatsApp. Kami balas secepatnya.",
  askAmount: false,
  askBank: false,
  submitLabel: "Kirim formulir",
};

const PRESET_SECTIONS: Record<PagePreset, SectionType[]> = {
  tentang: ["about", "stats", "testimonials", "cta"],
  layanan: ["services", "pricing", "faq", "cta"],
  kontak: ["contact", "location", "opening-hours"],
  donasi: ["donation"],
  formulir: ["donation"],
  kosong: [],
};

export function addPage(def: Def, preset: PagePreset, title: string, ctx?: DefaultsContext, inNav = true): { def: Def; pageId: string } {
  const pageId = nextPageId(def, slugifyTitle(title) || preset);
  const slug = uniquePageSlug(def, slugifyTitle(title) || preset);
  const home = def.pages.find((p) => p.slug === "/") ?? def.pages[0];
  let working = produce(def, (d) => {
    d.pages.push({ id: pageId, slug, title: title.slice(0, 80) || "Halaman", seo: { title: `${title} – ${d.site.name}`.slice(0, 70), description: "" }, ...(inNav ? {} : { hideFromNav: true }), sections: [] });
  });
  // Shared chrome: copy navbar/footer content from the home page (new ids), else defaults.
  const chrome = (type: "navbar" | "footer") => home?.sections.find((s) => s.type === type);
  for (const type of ["navbar", ...PRESET_SECTIONS[preset], "footer"] as SectionType[]) {
    const src = type === "navbar" || type === "footer" ? chrome(type) : undefined;
    if (src) {
      const page = working.pages.find((p) => p.id === pageId)!;
      const copy = { ...structuredClone(src), id: nextSectionId(working, type), anchor: uniqueAnchor(page, src.anchor) } as SectionNode;
      working = produce(working, (d) => {
        const list = d.pages.find((p) => p.id === pageId)!.sections;
        if (type === "navbar") list.unshift(copy as never);
        else list.push(copy as never);
      });
    } else {
      const added = addSection(working, pageId, type, undefined, ctx);
      working = added.def;
      // "Formulir" is the same section as "Donasi", set up as a plain form: no amount, no account.
      if (preset === "formulir" && type === "donation" && added.id) {
        const id = added.id;
        working = produce(working, (d) => {
          const s = d.pages.find((p) => p.id === pageId)!.sections.find((x) => x.id === id);
          if (s?.type === "donation") s.content = { ...FORM_CONTENT, accounts: [] };
        });
      }
    }
  }
  return { def: working, pageId };
}

export function removePage(def: Def, pageId: string): Def {
  const page = def.pages.find((p) => p.id === pageId);
  if (!page || page.slug === "/" || def.pages.length <= 1) return def;
  return produce(def, (d) => {
    d.pages = d.pages.filter((p) => p.id !== pageId);
  });
}

export function renamePage(def: Def, pageId: string, title: string): Def {
  return produce(def, (d) => {
    const p = d.pages.find((x) => x.id === pageId);
    if (p) p.title = title.slice(0, 80);
  });
}

/** Show or hide a page in the top menu. The home page is always in the menu. */
export function setPageInNav(def: Def, pageId: string, show: boolean): Def {
  return produce(def, (d) => {
    const p = d.pages.find((x) => x.id === pageId);
    if (!p || p.slug === "/") return;
    if (show) delete p.hideFromNav;
    else p.hideFromNav = true;
  });
}

export function updatePageSeo(def: Def, pageId: string, patch: Partial<PageNode["seo"]>): Def {
  return produce(def, (d) => {
    const p = d.pages.find((x) => x.id === pageId);
    if (!p) return;
    for (const [k, v] of Object.entries(patch) as Array<[keyof PageNode["seo"], unknown]>) {
      if (v === undefined || v === false) delete p.seo[k];
      else (p.seo as Record<string, unknown>)[k] = v;
    }
  });
}
